import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { analytics } from "../lib/posthog";
import { useAuthStore } from "../stores/useAuthStore";

/**
 * Manually captures a PostHog $pageview on every route change, for
 * authenticated users only.
 *
 * PostHog's own pageview autocapture only fires on a full page load, which
 * never happens again after the initial load in this SPA (react-router
 * handles all subsequent navigation client-side) — so route changes would
 * otherwise go completely untracked. initPostHog() sets `capture_pageview:
 * false` to disable the automatic (browser-load-only) behavior in favor of
 * this explicit, route-aware one.
 *
 * Gated on isAuthenticated: only identified-user activity is meaningful
 * here (see docs/analytics/event-tracking-plan.md) — capturing pageviews
 * for anonymous /sign-in bounces was creating unwanted anonymous noise
 * regardless of PostHog's person_profiles setting, so it's suppressed at
 * the source instead of relying on that config to filter it out downstream.
 */
export function usePostHogPageview(): void {
  const { pathname, search } = useLocation();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) return;

    analytics.capture("$pageview", {
      // No friendly page-title registry exists in this app yet (no
      // per-route document.title), so the route path is the best
      // available stand-in for "page name" — product asked for this
      // specifically rather than relying only on PostHog's own
      // $pathname/$current_url default properties.
      page_name: pathname,
      page_link: window.location.href,
    });
  }, [pathname, search, isAuthenticated]);
}
